import React, { useEffect } from "react";
import { SqlLineage } from "../../../components/lineage";
import { getMoneySavings } from "@/pages/explorer/utils";
import { useAppState } from "@/modules/app/useAppContext";
import classNames from "classnames";
import styles from "../styles.module.scss";
import { InfoIcon } from "@/assets/icons";
import { GetQuerySQLLineageResponse, QueryTagMeta } from "@/pages/explorer/types";

export type { QueryTagMeta };

export type SqlLineageNodeDetail = {
  type: string;
  node_cost?: number;
  columns?: { name: string; expression?: string; description?: string }[];
  indices?: unknown;
  is_lineage?: boolean;
};

export type SqlLineageProps = {
  query_tags_meta: { [key: string]: QueryTagMeta[] };
  details: Record<string, SqlLineageNodeDetail>;
  tableEdges: [string, string][];
  nodePositions: Record<string, [number, number]>;
  is_error: boolean;
};

type QuerySqlLineageProps = {
  sqlLineage: SqlLineageProps | null;
  selectedInsight: string | null;
  showSideContent: boolean;
  onNodeSelect: (node: string) => void;
};

type PercentValue = { percent: string; value: string; tooltip: string };

const QuerySqlLineage: React.FC<QuerySqlLineageProps> = ({
  sqlLineage,
  selectedInsight,
  showSideContent,
  onNodeSelect,
}) => {
  const { currency } = useAppState();
  useEffect(() => {
    if (!sqlLineage) return;

    const { query_tags_meta } = sqlLineage;
    if (selectedInsight) {
      const insightNodes: string[] = [];
      const nodesSavingsPerformance: Record<
        string,
        { savings?: PercentValue; performance?: PercentValue }
      > = {};

      for (const key in query_tags_meta) {
        for (const t of query_tags_meta[key] || []) {
          if (t.tag_rk !== selectedInsight) continue;
          insightNodes.push(key);
          if (t.cost_savings_ui || t.time_savings_ui) {
            nodesSavingsPerformance[key] = nodesSavingsPerformance[key] || {};
          }
          if (t.cost_savings_ui) {
            nodesSavingsPerformance[key]["savings"] = {
              percent: t.cost_savings_ui.percent,
              value: getMoneySavings(
                t.cost_savings_min ?? "0",
                t.cost_savings_max ?? "0",
                currency
              ),
              tooltip: t.cost_savings_ui.tooltip,
            };
          }
          if (t.time_savings_ui) {
            nodesSavingsPerformance[key]["performance"] = t.time_savings_ui;
          }
        }
      }

      document.dispatchEvent(new CustomEvent("onHighlightedNodes", { detail: insightNodes }));
      document.dispatchEvent(
        new CustomEvent("onNodesSavingsPerformance", {
          detail: nodesSavingsPerformance,
        })
      );
    } else {
      const nodesCost: { [key: string]: number } = {};
      for (const key in sqlLineage.details) {
        if (sqlLineage.details[key].node_cost) nodesCost[key] = sqlLineage.details[key].node_cost;
      }
      document.dispatchEvent(new CustomEvent("onNodesCost", { detail: nodesCost }));
    }
  }, [selectedInsight, sqlLineage]);

  useEffect(() => {
    document.dispatchEvent(new CustomEvent("fitView"));
  }, [showSideContent]);

  useEffect(() => {
    const handleSelectedNode = (e: CustomEvent) => {
      const node = e.detail;
      onNodeSelect(node);
      document.dispatchEvent(new CustomEvent("onSelectedNodes", { detail: node }));
    };

    // @ts-ignore
    document.addEventListener("selectedNode", handleSelectedNode);
    return () => {
      // @ts-ignore
      document.removeEventListener("selectedNode", handleSelectedNode);
    };
  }, []);

  if (!sqlLineage) return null;

  return (
    <div className="al-position-relative">
      {sqlLineage?.is_error && (
        <div
          className={classNames(
            "position-absolute al-flex gap-sm al-items-center",
            styles.sql_lineage_error_overlay
          )}
        >
          <InfoIcon />
          <div>Simplified view shown. Query contains unsupported keywords or constructs.</div>
        </div>
      )}
      <SqlLineage
        details={sqlLineage.details}
        tableEdges={sqlLineage.tableEdges}
        nodePositions={sqlLineage.nodePositions}
        height="50vh"
        externalSidePanel
      />
    </div>
  );
};

export function toSqlLineageProps(lineage: GetQuerySQLLineageResponse): SqlLineageProps {
  return {
    query_tags_meta: lineage.query_tags_meta,
    details: lineage.details as Record<string, SqlLineageNodeDetail>,
    tableEdges: lineage.tableEdges,
    nodePositions: lineage.nodePositions ?? {},
    is_error: lineage.is_error ?? false,
  };
}

export { QuerySqlLineage };
